import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import type { DeviceDoc, DevicePlatform } from '../types/device';

const getPlatform = (): DevicePlatform => {
  const p = Capacitor.getPlatform();
  if (p === 'android' || p === 'ios') return p;
  return 'web';
};

export const getNativeDeviceId = async (): Promise<string | null> => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { identifier } = await Device.getId();
    return identifier || null;
  } catch {
    return null;
  }
};

export const registerOrUpdateDevice = async (params: {
  userId: string;
  memberId: string | null;
  familyId: string | null;
  pushToken: string | null;
  isHomeDevice: boolean;
  isPrimaryForUser: boolean;
}): Promise<{ deviceId: string | null }> => {
  const deviceId = await getNativeDeviceId();
  if (!deviceId) return { deviceId: null };

  const payload: DeviceDoc = {
    userId: params.userId,
    memberId: params.memberId,
    familyId: params.familyId,
    platform: getPlatform(),
    pushToken: params.pushToken,
    isHomeDevice: params.isHomeDevice,
    isPrimaryForUser: params.isPrimaryForUser,
    updatedAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
  };

  await setDoc(
    doc(db, 'devices', deviceId),
    {
      ...payload,
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { deviceId };
};

export const getNativePushToken = async (): Promise<string | null> => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    if (Capacitor.getPlatform() === 'ios') {
      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      const perm = await FirebaseMessaging.checkPermissions();
      if (perm.receive !== 'granted') return null;
      const res = await FirebaseMessaging.getToken();
      return res?.token || null;
    }

    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') return null;

    const token = await new Promise<string>((resolve, reject) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (resolved) return;
        resolved = true;
        reject(new Error('Push registration timeout'));
      }, 15000);

      const reg = (PushNotifications as any).addListener('registration', (tkn: any) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeout);
        resolve(tkn.value);
      });

      const regErr = (PushNotifications as any).addListener('registrationError', (err: any) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeout);
        reject(new Error(typeof err === 'string' ? err : err?.message || 'registrationError'));
      });

      PushNotifications.register().catch((e) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeout);
        reject(e);
      });

      // best-effort cleanup
      void reg;
      void regErr;
    });

    return token || null;
  } catch {
    return null;
  }
};
